import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import { IcdCode } from '../Models/Api/Models/IcdCode';
import { Component, Output, Input, EventEmitter, forwardRef, ElementRef, ViewChild, Injector } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl, FormControl } from '@angular/forms';
import { GlobalValidator } from '../Classes/globalValidator';
import { SpaContext } from 'Public_old/src/app/Classes/spacontext';
import { RedRobinService } from 'Public_old/src/app/Services/red-robin.service';
import { OutcomesService } from '../../Services/outcomes.service';

interface IcdOption extends IcdCode {
	selected: boolean;
}

@Component({
	standalone: false,
	selector: 'visiba-icd',
	template: `
		<div class="subSection">
			<div class="wallInput wallStyling inputWrap" [class.hasIcon]="icon != null">
				<input
					type="text"
					[readonly]="false"
					[(ngModel)]="searchValue"
					[placeholder]="localPlaceholder"
					(ngModelChange)="search($event)"
					(click)="handleSearchbarClicked()"
					(keydown.arrowdown)="arrowMove(1); prevent($event)"
					(keydown.arrowup)="arrowMove(-1); prevent($event)"
					(keyup.esc)="closeList()"
					(keyup.enter)="showOptions === true ? selectOption(targetedOption) : openList(); prevent($event)"
					(keydown.enter)="prevent($event)"
					(blur)="blur()"
				/>

				<div #ulWrap class="ulWrap" [hidden]="!showOptions">
					<div
						*ngFor="let entry of matchedIcdCodes"
						[class.targeted]="targetedOption?.Entry === entry.Entry"
						(mouseover)="mouseoverOption(entry)"
						(click)="selectOption(entry)"
					>
						<span class="code">{{ entry.Code }}</span>
						<span class="desc">{{ entry.Description }}</span>
					</div>
				</div>
			</div>
		</div>
		<div class="subSection">
			<div *ngFor="let entry of searchedDiagnoses">
				<visiba-input
					type="checkbox"
					[(ngModel)]="entry.selected"
					(ngModelChange)="this.handleEntryChanged()"
					[staticplaceholder]="entry.Entry"
				></visiba-input>
			</div>
		</div>
		<!-- If RR -->
		<ng-container *ngIf="showRRContent">
			<div class="subSection">
				<div class="textblock margin-bottom">
					{{ 'LicenceHolder.AppointmentEvaluation.Title.DiagnosisSuggestions' | translate }}
				</div>
				<div *ngFor="let entry of suggestedDiagnoses">
					<visiba-input
						type="checkbox"
						[(ngModel)]="entry.selected"
						(ngModelChange)="this.handleEntryChanged()"
						[staticplaceholder]="entry.Entry"
					></visiba-input>
				</div>
			</div>
			<div class="subSection">
				<div class="textblock margin-bottom">
					{{ 'LicenceHolder.AppointmentEvaluation.Title.RDiagnosisSuggestions' | translate }}
				</div>
				<div *ngFor="let entry of suggestedSymptomDiagnoses">
					<visiba-input
						type="checkbox"
						[(ngModel)]="entry.selected"
						(ngModelChange)="this.handleEntryChanged()"
						[staticplaceholder]="entry.Entry"
					></visiba-input>
				</div>
			</div>
		</ng-container>
	`,
	host: {},
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => VisibaICDComponent),
			multi: true,
		},
	],
})
export class VisibaICDComponent {
	@Input() icon: string = null;
	@Input() placeholder = '';
	@Input() errorMessages: { [name: string]: string } | string = 'default';
	@Output() onChange = new EventEmitter<IcdCode[]>();
	@Output('blur') onBlurEvent = new EventEmitter();
	@ViewChild('ulWrap', { static: false }) ulWrap: ElementRef;

	public localPlaceholder: string = null;
	public targetedOption: IcdCode = null;
	public showOptions = false;
	public currentError: string = null;
	public searchValue = '';

	public suggestedDiagnoses: IcdOption[] = [];
	public suggestedSymptomDiagnoses: IcdOption[] = [];
	public searchedDiagnoses: IcdOption[] = [];

	public matchedIcdCodes: IcdCode[] = [];
	public showRRContent = false;

	private debounceTimer = null;

	constructor(
		public spaContext: SpaContext,
		private elementRef: ElementRef,
		private outcomesService: OutcomesService,
		private injector: Injector,
		private translateService: TranslateService,
		private redRobinService: RedRobinService,
	) {}

	public ngOnInit(): void {
		this.localPlaceholder = this.translateService.get(this.placeholder);
	}

	public getRRSessionsOutcomes(RRsessionId: string): void {
		this.redRobinService.getSessionOutcome(RRsessionId).subscribe(
			(response) => {
				const unsortedSuggestedDiagnoses: IcdCode[] = [];
				const unsortedSuggestedSymptomDiagnoses: IcdCode[] = [];
				response.diagnoses.forEach((diagnose) => {
					const icdCode = <IcdCode>{
						Code: diagnose.icd10,
						Description: diagnose.name,
						Entry: `${diagnose.icd10} ${diagnose.name}`,
						Probability: diagnose.probability,
						SuggestedFromRedRobin: true,
					};
					unsortedSuggestedDiagnoses.push(icdCode);
				});
				response.symptomDiagnoses.forEach((diagnose) => {
					const icdCode = <IcdCode>{
						Code: diagnose.icd10,
						Description: diagnose.name,
						Entry: `${diagnose.icd10} ${diagnose.name}`,
						Probability: diagnose.probability,
						SuggestedFromRedRobin: true,
					};
					unsortedSuggestedSymptomDiagnoses.push(icdCode);
				});

				this.suggestedDiagnoses = this.sortDiagnosesOnProbabilityDescending(unsortedSuggestedDiagnoses).map((diagnose) => {
					return { ...diagnose, selected: false };
				});
				this.suggestedSymptomDiagnoses = this.sortDiagnosesOnProbabilityDescending(unsortedSuggestedSymptomDiagnoses).map(
					(diagnose) => {
						return { ...diagnose, selected: false };
					},
				);

				this.showRRContent = true;
			},
			() => {
				// noop
			},
		);
	}

	public writeValue(): void {
		// noop
	}
	//Call to push value
	public propagateChange = (_: unknown): void => {
		// noop
	};
	public registerOnChange(fn: (_: unknown) => void): void {
		this.propagateChange = fn;
	}
	public registerOnTouch(): void {
		// noop
	}
	public registerOnTouched(): void {
		// noop
	}

	public emitValue(val: IcdCode[]): void {
		this.propagateChange(val);

		this.handleErrors();

		if (typeof this.onChange !== 'undefined') {
			window.setTimeout(() => {
				this.onChange.emit(val);
			}, 10);
		}
	}

	public handleErrors(): void {
		this.currentError = GlobalValidator.validateFormControl(
			this.translateService,
			this.injector.get(NgControl).control as FormControl,
			this.errorMessages,
		);
	}

	public blur(): void {
		window.setTimeout(() => {
			if (this.showOptions) {
				this.blur();
			} else {
				this.onBlurEvent.emit();
			}
		}, 10);
	}

	public mouseoverOption(option: IcdCode): void {
		this.targetedOption = option;
	}

	public selectOption(option: IcdCode): void {
		if (!option) return;
		this.addIcdCode(option);
		this.closeList();
		this.searchValue = '';
		this.emitValue(this.getChosenDiagnoses());
	}

	public search(text: string): void {
		if (text.length <= 2) {
			this.matchedIcdCodes = [];
		} else {
			if (this.debounceTimer) clearTimeout(this.debounceTimer);

			this.debounceTimer = setTimeout(() => {
				this.outcomesService.searchIcdCodes(text, 15).subscribe((searchResults) => {
					this.matchedIcdCodes = searchResults.data.filter((code) => {
						return (
							!this.searchedDiagnoses.find((searched) => searched.Code === code.Code) &&
							!this.suggestedDiagnoses.find((suggested) => suggested.Code === code.Code)
						);
					});

					if (this.matchedIcdCodes.length > 0) {
						this.openList();
					} else {
						this.closeList();
					}
				});
			}, 250);
		}
	}

	public handleSearchbarClicked(): void {
		if (this.showOptions) {
			this.closeList();
		} else {
			this.search(this.searchValue);
		}
	}

	public openList(): void {
		if (this.showOptions || this.matchedIcdCodes.length === 0) return;

		this.showOptions = true;
		window.setTimeout(() => {
			const doc = document.getElementsByTagName('html')[0];
			doc.addEventListener('click', this.listClickCheck, false);
		});
	}
	public listClickCheck(event: Event): void {
		if (
			this.showOptions &&
			event.target &&
			this.elementRef.nativeElement !== event.target &&
			!this.elementRef.nativeElement.contains(event.target)
		) {
			this.closeList();
		}
	}

	public closeList(): void {
		this.showOptions = false;
		const doc = document.getElementsByTagName('html')[0];
		doc.removeEventListener('click', this.listClickCheck, false);
		this.targetedOption = null;
	}

	public arrowMove(direction: number): void {
		let pos = -1;
		if (this.targetedOption.Entry !== null)
			pos = this.matchedIcdCodes.map((IcdCode) => IcdCode.Entry).indexOf(this.targetedOption.Entry);

		const newPos = pos + direction;

		if (newPos >= 0 && newPos < this.matchedIcdCodes.length) this.targetedOption = this.matchedIcdCodes[newPos];

		this.openList();

		const targetElement = this.ulWrap.nativeElement.querySelector('.targeted');
		if (targetElement !== null) {
			this.ulWrap.nativeElement.scrollTop = targetElement.offsetTop - targetElement.clientHeight;
		}
	}

	public addIcdCode(icdCode: IcdCode): void {
		if (this.suggestedDiagnoses.find((c) => c.Code === icdCode.Code)) this.suggestedDiagnoses.push({ ...icdCode, selected: true });
		else if (this.suggestedSymptomDiagnoses.find((c) => c.Code === icdCode.Code))
			this.suggestedSymptomDiagnoses.push({ ...icdCode, selected: true });
		else this.searchedDiagnoses.push({ ...icdCode, selected: true });
	}

	public getChosenDiagnoses(): IcdCode[] {
		const selectedCodes = [...this.suggestedDiagnoses, ...this.suggestedSymptomDiagnoses, ...this.searchedDiagnoses].filter(
			(code) => code.selected,
		);

		return selectedCodes;
	}

	public handleEntryChanged(): void {
		this.emitValue(this.getChosenDiagnoses());
	}

	public prevent(event: KeyboardEvent): void {
		event.stopPropagation();
		event.preventDefault();
	}

	private sortDiagnosesOnProbabilityDescending(unsorted: IcdCode[]): IcdCode[] {
		const sorted = unsorted.sort((a, b) => b.Probability - a.Probability);

		return sorted;
	}
}
